import { TYPE } from '@kontentino/kontentino-constants/Pages';
import type {
  AnalyticsPlatform,
  PerformancePage,
} from 'api/new-analytics/types';
import PageTypeUtils from 'app/utils/pageType';
import NumberUtil from 'utils/number';
import type {
  PerformanceChartData,
  PerformanceChartDataset,
} from 'app/modules/analytics/modules/performance/types/chartTypes';
import { CHART_COLORS } from 'app/modules/analytics/components/shared/charts/chartConfig';
import {
  buildTimeAxis,
  collectSortedDates,
  valuesForDates,
} from 'app/modules/analytics/modules/performance/utils/timeSeriesHelpers';
import { sumTimeSeriesForPagesMetric } from 'app/modules/analytics/modules/performance/utils/sumPerformancePages';

const PLATFORM_TO_TYPE: Record<PerformancePage['platform'], number> = {
  facebook: TYPE.FACEBOOK,
  instagram: TYPE.INSTAGRAM,
  linkedin: TYPE.LINKEDIN,
  tiktok: TYPE.TIKTOK,
};

const PLATFORM_ORDER: AnalyticsPlatform[] = [
  'facebook',
  'instagram',
  'linkedin',
  'tiktok',
];

function colorForAnalyticsPlatform(platform: AnalyticsPlatform): string {
  const byPlatform: Partial<Record<AnalyticsPlatform, string>> = {
    facebook: CHART_COLORS.defaultDark,
    instagram: CHART_COLORS.tertiary,
    linkedin: CHART_COLORS.compareLight,
  };
  return byPlatform[platform] ?? CHART_COLORS.defaultDark;
}

function platformDisplayLabel(platform: AnalyticsPlatform): string {
  const type = PLATFORM_TO_TYPE[platform] ?? TYPE.FACEBOOK;
  return PageTypeUtils.getLabel(type);
}

function legendLabelForPlatformGroup(
  platform: AnalyticsPlatform,
  platformPages: PerformancePage[],
  metricKey: string,
): string {
  const platformName = platformDisplayLabel(platform);
  let total = 0;
  let hasValue = false;
  for (const p of platformPages) {
    const v = p.overview?.[metricKey]?.value;
    if (typeof v === 'number' && Number.isFinite(v)) {
      hasValue = true;
      total += v;
    }
  }
  return hasValue
    ? `${platformName} ${NumberUtil.compact(total)}`
    : platformName;
}

/**
 * One series per network: sums daily values for all pages of that `platform` in the group.
 */
export function buildMultiSeriesByPlatform(
  pages: PerformancePage[],
  metricKey: string,
): PerformanceChartData {
  if (pages.length === 0) {
    return { labels: [], datasets: [] };
  }

  const dates = collectSortedDates(
    pages.map((page) => page.time_series?.[metricKey]),
  );
  if (dates.length === 0) {
    return { labels: [], datasets: [] };
  }

  const byPlatform = new Map<AnalyticsPlatform, PerformancePage[]>();
  for (const p of pages) {
    const list = byPlatform.get(p.platform) ?? [];
    list.push(p);
    byPlatform.set(p.platform, list);
  }

  const datasets: PerformanceChartDataset[] = [];

  for (const platform of PLATFORM_ORDER) {
    const platformPages = byPlatform.get(platform);
    if (!platformPages?.length) {
      continue;
    }

    const merged = sumTimeSeriesForPagesMetric(platformPages, metricKey);
    const data = valuesForDates(merged, dates);

    datasets.push({
      label: legendLabelForPlatformGroup(platform, platformPages, metricKey),
      data,
      color: colorForAnalyticsPlatform(platform),
      platform,
    });
  }

  return { ...buildTimeAxis(dates), datasets };
}

export function buildStackedBarDatasetsByPlatform(
  pages: PerformancePage[],
  metricKey: string,
): PerformanceChartData {
  return buildMultiSeriesByPlatform(pages, metricKey);
}
